import { supabase } from '../lib/supabase';
import { logAuditEvent } from './auditLogService';
import { checkAvailableCredits as checkCredits, consumeCredit as consumeCreditBase } from './caseService';

/**
 * Credit Service
 * Enhanced credit management with transaction history
 */

/**
 * Check available credits (re-export from caseService)
 */
export async function checkAvailableCredits(userId) {
  return checkCredits(userId);
}

/**
 * Get credit breakdown by type
 */
export async function getCreditBreakdown(userId) {
  try {
    const { data, error } = await supabase
      ?.from('user_subscriptions')
      ?.select('trial_credits, paid_credits, monthly_credits')
      ?.eq('user_id', userId)
      ?.single();
    
    if (error) throw error;
    
    return {
      success: true,
      breakdown: {
        trial: data?.trial_credits || 0,
        paid: data?.paid_credits || 0,
        monthly: data?.monthly_credits || 0,
        total: (data?.trial_credits || 0) + (data?.paid_credits || 0) + (data?.monthly_credits || 0)
      }
    };
  } catch (error) {
    console.error('Get credit breakdown error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Consume credit with transaction logging
 */
export async function consumeCredit(userId, caseId, description = 'Case report generation') {
  try {
    // Check if user is a test account or has pending/approved test account status
    const { data: userProfile } = await supabase
      ?.from('user_profiles')
      ?.select('is_test_account, test_account_status')
      ?.eq('id', userId)
      ?.single();
    
    // Skip credit deduction for test accounts or pending/approved test account requests
    if (userProfile?.is_test_account || userProfile?.test_account_status === 'pending' || userProfile?.test_account_status === 'approved') {
      console.log('Test account - skipping credit deduction');
      return { success: true, creditType: 'test', isTestAccount: true };
    }
    
    // Consume credit using existing function
    const result = await consumeCreditBase(userId, caseId);
    
    if (!result?.success) {
      throw new Error(result?.error);
    }
    
    // Record transaction
    await supabase?.rpc('record_credit_transaction', {
      p_user_id: userId,
      p_transaction_type: 'debit',
      p_credit_type: result?.creditType || 'trial',
      p_amount: -1,
      p_description: description,
      p_case_id: caseId
    });
    
    logAuditEvent({ actionType: 'payment.credit_consumed', actionDescription: `Credit consumed for case`, entityType: 'case', entityId: caseId, metadata: { creditType: result?.creditType }, severity: 'info' }).catch(console.error);
    return { success: true, creditType: result?.creditType };
  } catch (error) {
    console.error('Consume credit error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Add credits with transaction logging
 */
export async function addCredits(userId, creditType, amount, description, paymentId = null, subscriptionId = null) {
  try {
    // H-09: Replace read-then-write with a single atomic DB call.
    // The old pattern read the current balance, added `amount` client-side,
    // and wrote the result back. Two concurrent Stripe webhook retries for
    // the same payment both read the same value and both wrote it + amount,
    // resulting in a single credit amount instead of double — but MORE
    // dangerously, a concurrent subscription + one-time purchase could each
    // overwrite the other's update with a stale read.
    // atomic_credit_add does the increment AND the transaction record in one
    // DB transaction, so concurrent callers always see each other's writes.
    const { error } = await supabase?.rpc('atomic_credit_add', {
      p_user_id:         userId,
      p_credit_type:     creditType,
      p_amount:          amount,
      p_description:     description,
      p_payment_id:      paymentId,
      p_subscription_id: subscriptionId,
    });

    if (error) throw error;

    logAuditEvent({ actionType: 'payment.credits_added', actionDescription: `${amount} ${creditType} credit(s) added: ${description}`, entityType: 'user', entityId: userId, metadata: { amount, creditType, paymentId, subscriptionId }, severity: 'warning' }).catch(console.error);
    return { success: true };
  } catch (error) {
    console.error('Add credits error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get credit transaction history
 */
export async function getCreditTransactionHistory(userId, limit = 50, offset = 0) {
  try {
    const { data, error, count } = await supabase
      ?.from('credit_transactions')
      ?.select('*', { count: 'exact' })
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false })
      ?.range(offset, offset + limit - 1);
    
    if (error) throw error;
    
    return { success: true, data, total: count };
  } catch (error) {
    console.error('Get credit transaction history error:', error);
    return { success: false, error: error?.message };
  }
}